import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { AppShell } from "../components/layout/AppShell";
import { NeuButton, NeuSelect } from "../components/neu/Neu";
import { listBranches } from "../api/directory";
import { useTodaySummary } from "../features/attendance/useTodaySummary";
import { TodayTable } from "../features/attendance/TodayTable";
import { TODAY_TABS, pickList } from "../features/attendance/todayLists";
import { useAsyncList } from "../hooks/useAsyncList";

// Dedicated screen for today's attendance, opened from the home board cards.
export default function TodayAttendancePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(TODAY_TABS.includes(params.get("tab")) ? params.get("tab") : "employees");
  const [branchId, setBranchId] = useState(params.get("branch") || "");
  const branches = useAsyncList(listBranches);
  const { rows, employees, date } = useTodaySummary(branchId);
  const list = pickList(tab, rows, employees);

  return (
    <AppShell
      title={t("dashboard.title")}
      subtitle={`${t("dashboard.todayNote")} ${date}`}
      actions={
        <NeuButton data-testid="today-back" className="flex items-center gap-2" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4" /> {t("dashboard.back")}
        </NeuButton>
      }
    >
      <div className="grid gap-5" data-testid="today-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2" data-testid="today-tabs">
            {TODAY_TABS.map((key) => (
              <button
                key={key}
                type="button"
                data-testid={`today-tab-${key}`}
                onClick={() => setTab(key)}
                className={`rounded-2xl border px-4 py-2 text-sm font-bold transition-[box-shadow,color] duration-300 ${
                  tab === key
                    ? "border-primary/50 text-primary shadow-neu-in"
                    : "border-border/60 text-muted-foreground shadow-neu-sm hover:text-foreground"
                }`}
              >
                {t(`dashboard.${key}`)}
              </button>
            ))}
          </div>
          <NeuSelect
            data-testid="today-branch-filter"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            className="min-w-48"
          >
            <option value="">{t("dashboard.allBranches")}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </NeuSelect>
        </div>

        <p className="text-xs text-muted-foreground" data-testid="today-count">
          {t(`dashboard.${tab}`)} · <span className="font-mono">{list.length}</span>
        </p>
        <TodayTable list={list} />
      </div>
    </AppShell>
  );
}
